/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import Place from "@mui/icons-material/Place";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { Button, CollapseCard } from "@totalsoft/rocket-ui";
import AddressContainer from "./AddressContainer";
import AddressesSummary from "./AddressesSummary";
import { withParams } from "../../../../providers/ParamsConsumer";
import {
  loadAddressTypes,
  loadAddressList,
  saveAddresses,
  loadCountries,
  loadPartnerAddressesSummary,
  consumeAddressFromOCR
} from "../../actionCreators";
import {
  getAddressTypes,
  getAddresses,
  getCountries,
  getPartnerAddressesSummary,
  getAddressFromOCR
} from "../../selectors";
import { shape, required, items, maxLength } from "@totalsoft/pure-validations";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";
import { isValid } from "@totalsoft/pure-validations-react";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { ErrorContainer } from "./AddressStyles";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

const AddressesContainer = ({
  partnerId,
  addresses,
  addressesLoaded,
  addressTypes,
  actions,
  countries,
  partnerAddressesSummary,
  summaryLoaded,
  setIsDirty,
  params,
  hasWritePermission,
  isIndividual,
  addressFromOCR
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  const [localAddresses, setLocalAddressList] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const [defaultAddressValidity, setDefaultAddressValidity] = useState(true);
  const [defaultAddressCount, setDefaultAddressCount] = useState(0);

  const getDefaultAddressesValidity = addressList =>
    !addressList ? true : addressList.filter(addr => addr.isDefault).length <= 1;

  const getDefaultAddressesCount = addressList =>
    !addressList ? 0 : addressList.filter(addr => addr.isDefault).length;

  const getCurrentAddressTypes = addressTypes => {
    const partnerAddressTypesBehaviourParam = params.partnerAddressTypes;
    const partnerTypeCode = isIndividual ? "PF" : "PJ";

    if (partnerAddressTypesBehaviourParam) {
      try {
        const configedAddressTypes = JSON.parse(partnerAddressTypesBehaviourParam)[partnerTypeCode];
        return addressTypes
          .filter(addr => configedAddressTypes.some(cat => cat.code === addr.addressTypeCode))
          .map(addr => ({
            ...addr,
            isDefault: !!configedAddressTypes.find(cat => cat.code === addr.addressTypeCode)?.isDefault
          }));
      } catch {
        // Catch Json errors
      }
    }

    return addressTypes;
  };

  const validatorAddressSelection = items(
    shape({
      addressTypeId: required,
      countryId: required,
      districtId: required,
      cityId: required,
      street: maxLength(255),
      streetNumber: maxLength(50),
      buildingNumber: maxLength(25),
      stairNumber: maxLength(50),
      flatNumber: maxLength(25),
      floorNumber: maxLength(50),
      zipCode: maxLength(25),
      neighborhood: maxLength(100)
    })
  );

  const [validationAddressSelection, validateAddressSelection] = useDirtyFieldValidation(
    validatorAddressSelection,
    dirtyInfo
  );

  const saveAddresses = (partnerId, localAddresses) => {
    if (!hasWritePermission) return;
    if (!validateAddressSelection(localAddresses)) {
      return;
    }
    actions.saveAddresses(partnerId, localAddresses, () => {
      setIsDirty(false);
    });
    setSaveDisabled(true);
  };
  const addAddress = () => {
    let addressesList = [...localAddresses, {}];
    setLocalAddressList(addressesList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const deleteAddress = item => {
    if (!hasWritePermission) return;
    let newAddressesList = localAddresses.filter(x => localAddresses.indexOf(x) !== localAddresses.indexOf(item));
    setLocalAddressList(newAddressesList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  useEffect(() => {
    validateAddressSelection(localAddresses, dirtyInfo);
  }, [localAddresses, dirtyInfo]);

  useEffect(() => {
    setDefaultAddressValidity(getDefaultAddressesValidity(localAddresses));
    setDefaultAddressCount(getDefaultAddressesCount(localAddresses));
  }, [localAddresses]);

  useEffect(() => {
    if (expanded && !addressesLoaded) {
      actions.loadAddressTypes();
      if (partnerId !== null) actions.loadAddressList(partnerId);
    }
  }, [expanded]);

  useEffect(() => {
    actions.loadCountries();
    if (partnerId !== null) actions.loadPartnerAddressesSummary(partnerId);
  }, []);

  useEffect(() => {
    if (addressesLoaded && localAddresses === null) {
      setLocalAddressList(addresses);
    }
  }, [addressesLoaded, addresses, localAddresses, setLocalAddressList]);

  useEffect(() => {
    if (
      addressFromOCR &&
      localAddresses !== null &&
      !localAddresses.some(x => JSON.stringify(x) === JSON.stringify(addressFromOCR))
    ) {
      const newLocalAddresses = [...localAddresses];
      newLocalAddresses.push({ ...addressFromOCR });
      setLocalAddressList(newLocalAddresses);
      setIsDirty(true);
      setSaveDisabled(false);
      actions.consumeAddressFromOCR();
    }
  }, [addressFromOCR, localAddresses, setLocalAddressList, setIsDirty, setSaveDisabled, actions]);

  useEffect(() => {
    if (!summaryLoaded && partnerId !== null) {
      actions.loadPartnerAddressesSummary(partnerId);
    }
  }, [summaryLoaded]);

  const onPartnerAddressPropertyChange = idx => (prop1, prop2) => (value1, value2) => {
    if (!hasWritePermission) return;
    const address = localAddresses[idx];
    setDirtyInfo(`${prop1}`);
    setLocalAddressList(
      Object.assign([], {
        ...localAddresses,
        [idx]: {
          ...localAddresses[idx],
          [prop1]: value1,
          ...(prop2 && { [prop2]: value2 }),
          ...(address.countryId !== value1 &&
            prop1 === "countryId" && { districtId: null, districtName: "", cityId: null, cityName: "" }),
          ...(address.districtId !== value1 && prop1 === "districtId" && { cityId: null, cityName: "" })
        }
      })
    );
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const ValidationError = () => (
    <>
      <ErrorContainer item xs={6}>
        {!defaultAddressValidity && t("PartnerAddress.DefaultAddressesError", { data: { count: defaultAddressCount } })}
      </ErrorContainer>
    </>
  );

  return (
    <CollapseCard
      id="addressesCard"
      icon={Place}
      iconColor="primary"
      title={t("Partner.Addresses")}
      subheader={<AddressesSummary partnerAddressesSummary={partnerAddressesSummary} />}
      onToggle={(_, expanded) => setExpanded(!expanded)}
      expanded={expanded}
      defaultExpanded={synchronizationInProgress}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container>
        <Grid item xs={12} mb={3}>
          <CardNoResultsWrapper content={localAddresses}>
            {localAddresses?.map((item, idx) => {
              return (
                <AddressContainer
                  key={idx}
                  item={item}
                  idx={idx}
                  countries={countries}
                  addressTypes={getCurrentAddressTypes(addressTypes)}
                  onPartnerAddressPropertyChange={onPartnerAddressPropertyChange(idx)}
                  deleteAddress={deleteAddress}
                  saveDisabled={saveDisabled}
                  setSaveDisabled={setSaveDisabled}
                  partnerAddressesSummary={partnerAddressesSummary}
                  validationAddressSelection={validationAddressSelection}
                  hasWritePermission={hasWritePermission}
                />
              );
            })}
          </CardNoResultsWrapper>
        </Grid>
        <Grid item xs={12}>
          <Button
            id="address_addBtn"
            color="primary"
            variant="contained"
            onClick={() => addAddress()}
            disabled={!hasWritePermission}
          >
            {t("Add")}
          </Button>
          <Button
            id="address_saveBtn"
            color="primary"
            variant="contained"
            onClick={() => saveAddresses(partnerId, localAddresses)}
            disabled={
              saveDisabled || !isValid(validationAddressSelection) || !defaultAddressValidity || !hasWritePermission
            }
            sx={{ marginLeft: ".25rem" }}
          >
            {t("Save")}
          </Button>
        </Grid>
        {!defaultAddressValidity && <ValidationError />}
      </Grid>
    </CollapseCard>
  );
};

AddressesContainer.propTypes = {
  partnerId: PropTypes.number,
  addressTypes: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  addresses: PropTypes.array,
  addressesLoaded: PropTypes.bool.isRequired,
  addressesLoading: PropTypes.bool.isRequired,
  deleteAddress: PropTypes.func,
  summaryLoaded: PropTypes.bool,
  partnerAddressesSummary: PropTypes.object,
  setIsDirty: PropTypes.func,
  countries: PropTypes.array,
  params: PropTypes.object.isRequired,
  isIndividual: PropTypes.bool,
  hasWritePermission: PropTypes.bool.isRequired,
  addressFromOCR: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const addressTypes = getAddressTypes(state);
  const {
    content: addresses,
    loaded: addressesLoaded,
    loading: addressesLoading
  } = getAddresses(state, ownProps.partnerId);
  const { content: countries, loaded: countriesLoaded, loading: countriesLoading } = getCountries(state);

  const { summaryLoaded, partnerAddressesSummary } = getPartnerAddressesSummary(state, ownProps.partnerId);
  const addressFromOCR = getAddressFromOCR(state, ownProps.partnerId);

  return {
    addressTypes,
    addresses,
    addressesLoaded,
    addressesLoading,
    countries,
    countriesLoaded,
    countriesLoading,
    summaryLoaded,
    partnerAddressesSummary,
    addressFromOCR
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadAddressTypes,
        loadAddressList,
        saveAddresses,
        loadCountries,
        loadPartnerAddressesSummary,
        consumeAddressFromOCR
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withParams(AddressesContainer));
